import React from "react"
import axios from "axios"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, Form, Input, Table } from "reactstrap"

import { Loading, PageContainer, Offers } from "../../components"
import { fail } from "../../actions"

const statusToColor = (status) => {
  switch (status) {
    case "active":
      return "success"
    case "suspended":
      return "danger"
    default:
      return "secondary"
  }
}

// Reseller view of one allocated customer: orders with editable quantity,
// read-only sales price, and the customer's buyable NCE catalog.
class ResellerCustomer extends React.Component {
  state = {
    fetching: true,
    orders: [],
    nceoffers: [],
    companyName: "",
  }

  componentDidMount = async () => {
    const { customerId } = this.props.match.params
    try {
      const [ordersRes, offersRes, customersRes] = await Promise.all([
        axios.get(`/resellercustomers/${customerId}/orders`),
        axios.get(`/resellercustomers/${customerId}/nceoffers`),
        axios.get("/resellercustomers"),
      ])
      const me = (customersRes.data.resellercustomers || []).find(
        (c) => c.customerId === customerId
      )
      this.setState({
        fetching: false,
        orders: (ordersRes.data.orders || []).map((o) => ({
          ...o,
          newQuantity: o.quantity,
        })),
        nceoffers: offersRes.data.nceoffers || [],
        companyName: me ? me.companyName : "",
      })
    } catch (error) {
      this.props.fail(null, error).then(() => this.setState({ fetching: false }))
    }
  }

  changeQuantity = (orderId, value) => {
    this.setState({
      orders: this.state.orders.map((o) =>
        o.id === orderId ? { ...o, newQuantity: value } : o
      ),
    })
  }

  updateQuantity = async (orderId, confirmScheduled = false) => {
    const { t } = this.props
    const { customerId } = this.props.match.params
    const order = this.state.orders.find((o) => o.id === orderId)
    if (!order) return
    const setOrder = (patch) =>
      this.setState({
        orders: this.state.orders.map((o) =>
          o.id === orderId ? { ...o, ...patch } : o
        ),
      })
    setOrder({ updating: true, success: false, error: false })
    try {
      await axios.post(`/licenses/${orderId.toLowerCase()}/quantity`, {
        customerId,
        quantity: Number(order.newQuantity),
        ...(confirmScheduled ? { confirmScheduled: true } : {}),
      })
      setOrder({
        updating: false,
        success: true,
        quantity: Number(order.newQuantity),
      })
    } catch (err) {
      const resp = err?.response
      if (resp?.status === 409 && resp.data?.requiresConfirmation) {
        setOrder({ updating: false })
        const message = t("_scheduledQuantityChangeConfirm", {
          quantity: resp.data.newQuantity,
          date: new Date(resp.data.effectiveDate).toLocaleDateString(),
        })
        if (window.confirm(message)) {
          return this.updateQuantity(orderId, true)
        }
        return
      }
      if (resp?.status === 409 && resp.data?.autoRenewDisabled) {
        setOrder({ updating: false, error: true })
        window.alert(t("_autoRenewDisabledNoSchedule"))
        return
      }
      this.props
        .fail(null, err)
        .then(() => setOrder({ updating: false, error: true }))
    }
  }

  render() {
    const { t } = this.props
    const { customerId } = this.props.match.params
    return (
      <PageContainer
        title={this.state.companyName || t("_customer")}
        loading={this.state.fetching}
      >
        <Link to="/reseller">{t("_back")}</Link>
        <h3 className="mt-3">{t("_orders")}</h3>
        {this.state.orders.length === 0 ? (
          <Alert color="info">{t("_noOrders")}</Alert>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("_name")}</th>
                <th>{t("_autoRenewDate")}</th>
                <th>{t("_quantity")}</th>
                <th>{t("_unitPrice")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {this.state.orders.map((o) => (
                <tr key={o.id}>
                  <td style={{ verticalAlign: "middle" }}>
                    {o.name}{" "}
                    <Badge color={statusToColor(o.status)}>
                      {t(`_${o.status}`)}
                    </Badge>
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {o.autoRenewEnabled && o.commitmentEndDate
                      ? new Date(o.commitmentEndDate).toLocaleDateString()
                      : "-"}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault()
                        this.updateQuantity(o.id)
                      }}
                      className="d-flex"
                    >
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        style={{ width: 110 }}
                        value={o.newQuantity}
                        disabled={o.updating || o.status !== "active"}
                        onChange={(e) =>
                          this.changeQuantity(o.id, e.target.value)
                        }
                      />
                    </Form>
                    {o.scheduledNextTermInstructions?.quantity != null && (
                      <Badge color="warning">
                        {`→ ${o.scheduledNextTermInstructions.quantity}`}
                      </Badge>
                    )}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {o.unitPrice != null ? Number(o.unitPrice).toFixed(2) : "-"}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {o.updating ? (
                      <Loading small />
                    ) : (
                      <Button
                        color={
                          o.success
                            ? "success"
                            : o.error
                            ? "danger"
                            : "primary"
                        }
                        disabled={
                          o.status !== "active" ||
                          Number(o.newQuantity) === Number(o.quantity)
                        }
                        onClick={() => this.updateQuantity(o.id)}
                      >
                        {t("_update")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <h3 className="mt-4">{t("_offers")}</h3>
        <Offers
          offers={this.state.nceoffers}
          link={(product) =>
            `#/reseller/customers/${customerId}/offers/${product._id}`
          }
        />
      </PageContainer>
    )
  }
}

export default connect(null, { fail })(withTranslation()(ResellerCustomer))
